import React from "react";
import { Link } from "react-router-dom";

import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import {
  homeStats,
  homeServices,
  trustPoints,
  productCategories,
  processSteps,
} from "../data/siteData";

const FEATURED_CATEGORIES = productCategories.slice(0, 3);

function Home() {
  return (
    <div className="page-shell">
      <SiteHeader />

      <main>

        {/* ── 1. HERO ─────────────────────────────────────────── */}
        <section className="section-hero">
          <div className="container home-hero-grid">

            <div className="home-hero-copy">
              <p className="eyebrow animate-fade-up">Wholesale Pharmaceutical Supply · Nigeria</p>
              <h1 className="animate-fade-up-delay-1">
                Reliable medicine supply for trade buyers.
              </h1>
              <p className="home-hero-text animate-fade-up-delay-2">
                Etiquette Pharmaceuticals Limited is a PCN-licensed wholesale
                distributor supplying generics, branded medicines, OTC products,
                and parallel imports to pharmacies, hospitals, clinics, and
                regional distributors across Nigeria.
              </p>

              <div className="btn-group animate-fade-up-delay-3">
                <Link to="/contact" className="btn btn-primary">
                  Request a Quote
                </Link>
                <Link to="/products" className="btn btn-outline">
                  View Products
                </Link>
              </div>

              <ul className="home-trust-list animate-fade-up-delay-3">
                {trustPoints.map((point) => (
                  <li key={point} className="home-trust-item">
                    <span className="bullet-dot bullet-dot-gold" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>

            <div className="home-hero-card animate-fade-up-delay-2">
              <div className="home-hero-card-inner">
                <p className="eyebrow" style={{ color: "rgba(255,255,255,0.5)" }}>
                  Wholesale mix
                </p>
                <div className="home-chip-row">
                  {["Generics", "OTC", "Branded", "Parallel Imports", "Consumables"].map((chip) => (
                    <span key={chip} className="home-chip">{chip}</span>
                  ))}
                </div>
                <hr className="home-hero-divider" />
                <p className="home-hero-card-label">Built for B2B buyers</p>
                <h3 className="home-hero-card-heading">
                  From enquiry to replenishment
                </h3>
                <p className="home-hero-card-text">
                  Procurement teams move from product enquiry to stock
                  confirmation, dispatch planning, and repeat ordering — with
                  a single point of contact throughout.
                </p>
                <Link to="/contact" className="btn btn-gold" style={{ marginTop: "1.25rem" }}>
                  Open an account →
                </Link>
              </div>
            </div>

          </div>
        </section>

        {/* ── 2. STATS BAR ────────────────────────────────────── */}
        <section className="section section-sm" style={{ background: "var(--white)" }}>
          <div className="container">
            <div className="stats-bar">
              {homeStats.map((stat) => (
                <div key={stat.label} className="stat-card">
                  <p className="stat-value">{stat.value}</p>
                  <p className="stat-label">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 3. SERVICES PREVIEW ─────────────────────────────── */}
        <section className="section section-grey">
          <div className="container">
            <div className="section-heading">
              <p className="eyebrow">What We Do</p>
              <h2>Wholesale services built for trade.</h2>
              <p>
                Every service we offer is structured around the needs of
                professional buyers — pharmacies, hospitals, clinics, and
                distributors who need dependable supply.
              </p>
            </div>

            <div className="home-services-grid">
              {homeServices.map((service, i) => (
                <article key={service.title} className="service-card">
                  <p className="service-card-number">0{i + 1}</p>
                  <h3>{service.title}</h3>
                  <p>{service.text}</p>
                </article>
              ))}
            </div>

            <div className="home-services-cta">
              <Link to="/services" className="btn btn-secondary">
                View all services →
              </Link>
            </div>
          </div>
        </section>

        {/* ── 4. ABOUT TEASER ─────────────────────────────────── */}
        <section className="section section-dark">
          <div className="container home-about-grid">

            <div className="home-about-copy">
              <p className="eyebrow">About Etiquette Pharmaceuticals</p>
              <h2>A wholesale partner built on compliance and continuity.</h2>
              <p>
                We are a PCN-licensed pharmaceutical wholesaler operating from
                Abuja, Nigeria. Our operations are structured around what trade
                buyers actually need — clear stock availability, fast responses,
                and reliable fulfilment from enquiry to dispatch.
              </p>
              <p style={{ marginTop: "1rem" }}>
                Registered in Nigeria (RC 1727250), we serve retail pharmacy
                chains, hospitals, specialist clinics, and regional distributors
                who need a dependable wholesale partner.
              </p>
              <div className="btn-group" style={{ marginTop: "2rem" }}>
                <Link to="/about" className="btn btn-outline">
                  Learn more about us →
                </Link>
              </div>
            </div>

            <div className="home-about-image-wrap">
              <img
                src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1200&auto=format&fit=crop"
                alt="Pharmaceutical warehouse operations"
                className="home-about-image"
              />
              <div className="home-about-badge">
                <span className="home-about-badge-value">PCN</span>
                <span className="home-about-badge-label">Licensed Distributor</span>
              </div>
            </div>

          </div>
        </section>

        {/* ── 5. PRODUCT CATEGORIES ───────────────────────────── */}
        <section className="section" style={{ background: "var(--white)" }}>
          <div className="container">
            <div className="section-heading">
              <p className="eyebrow">Product Catalogue</p>
              <h2>A structured portfolio for every buyer type.</h2>
              <p>
                From fast-moving generics to parallel imports and seasonal
                demand lines — our catalogue is organised for procurement teams
                who need clarity and speed.
              </p>
            </div>

            {/* Category pills */}
            <div className="home-category-pills">
              {productCategories.map((cat) => (
                <Link key={cat.id} to="/products" className="home-category-pill">
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Featured 3 cards */}
            <div className="grid-3 home-category-cards">
              {FEATURED_CATEGORIES.map((cat) => (
                <article key={cat.id} className="category-card">
                  <span className="tag">{cat.tag}</span>
                  <h3>{cat.name}</h3>
                  <p>{cat.description}</p>
                  <ul className="category-items">
                    {cat.items.slice(0, 4).map((item) => (
                      <li key={item} className="category-item">{item}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>

            <div className="home-services-cta">
              <Link to="/products" className="btn btn-secondary">
                View full catalogue →
              </Link>
            </div>
          </div>
        </section>

        {/* ── 6. PROCESS STEPS ────────────────────────────────── */}
        <section className="section section-dark">
          <div className="container">
            <div className="section-heading centered">
              <p className="eyebrow">How It Works</p>
              <h2>From enquiry to ongoing supply.</h2>
              <p>
                A clear four-step process designed to get trade buyers from
                first contact to confirmed fulfilment as efficiently as possible.
              </p>
            </div>

            <div className="grid-4 home-process-grid">
              {processSteps.map((step, i) => (
                <article key={step.step} className="timeline-card">
                  {i < processSteps.length - 1 && (
                    <span className="home-process-connector" aria-hidden="true" />
                  )}
                  <p className="step-number">{step.step}</p>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ── 7. CTA BANNER ───────────────────────────────────── */}
        <section className="section section-green">
          <div className="container cta-banner">
            <p className="eyebrow" style={{ color: "rgba(255,255,255,0.6)" }}>
              Ready to work with us?
            </p>
            <h2>Open a wholesale account today.</h2>
            <p>
              Send us your product requirements and organisation details.
              Our team will confirm availability and get back to you within
              24 hours.
            </p>
            <div className="btn-group" style={{ justifyContent: "center" }}>
              <Link to="/contact" className="btn btn-outline">
                Request a Quote
              </Link>
              <Link to="/products" className="btn btn-ghost" style={{ color: "rgba(255,255,255,0.8)" }}>
                Browse products →
              </Link>
            </div>
          </div>
        </section>

      </main>

      <SiteFooter />
    </div>
  );
}

export default Home;
